import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MmscanThemeService } from '@mmscan/angular-ui/core';

export interface MmscanLogo {
  /** Image URL (SVG recommended). */
  src: string;
  /** Image used while the dark theme is on. */
  darkSrc?: string;
  alt: string;
  /** Router link; the logo then becomes the home link. */
  route?: string | readonly unknown[];
  /** Intrinsic size, to reserve space while loading. Displayed height comes from CSS. */
  width?: number;
  height?: number;
}

/** A logo image that follows the theme, optionally linking home. Size it with a height class. */
@Component({
  selector: 'mmscan-logo',
  imports: [RouterLink],
  template: `
    @if (logo().route; as route) {
      <a
        [routerLink]="route"
        class="inline-flex h-full rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        <img
          [src]="src()"
          [alt]="logo().alt"
          [attr.width]="logo().width"
          [attr.height]="logo().height"
          class="h-full w-auto"
        />
      </a>
    } @else {
      <img
        [src]="src()"
        [alt]="logo().alt"
        [attr.width]="logo().width"
        [attr.height]="logo().height"
        class="h-full w-auto"
      />
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'inline-flex shrink-0' },
})
export class MmscanLogoImage {
  private readonly theme = inject(MmscanThemeService);

  readonly logo = input.required<MmscanLogo>();

  protected readonly src = computed(() => {
    const { src, darkSrc } = this.logo();
    return this.theme.theme() === 'dark' && darkSrc ? darkSrc : src;
  });
}
